import { useEffect, useState } from "react";
import { Modal } from "./Modal";

// Discoverable reference for the tree's keyboard shortcuts.  Opens via the
// "?" key (outside text fields) or the ⌨ button in the tree header.  The
// shortcuts themselves live in TreeView's onKeyDown — keep this list in
// sync when bindings change.

const GROUPS: { title: string; rows: [string, string][] }[] = [
  {
    title: "Navigate",
    rows: [
      ["↑ / ↓", "Move between tasks"],
      ["← / →", "Collapse / expand"],
      ["Esc", "Close the detail pane"],
    ],
  },
  {
    title: "Edit",
    rows: [
      ["Enter", "Rename task"],
      ["⌘ Enter", "New sibling task"],
      ["⌘⇧ Enter", "New sub-task"],
      ["Tab / ⇧Tab", "Indent / outdent"],
      ["Space", "Toggle complete"],
      ["Delete", "Delete (with Undo)"],
    ],
  },
  {
    title: "Select",
    rows: [
      ["⇧ Click", "Select a range"],
      ["⌃/⌘ Click", "Add to selection"],
      ["Long-press + drag", "Select on touch"],
    ],
  },
];

export function ShortcutHelpModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="Keyboard shortcuts" onClose={onClose}>
      <div className="space-y-4">
        {GROUPS.map((g) => (
          <section key={g.title}>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {g.title}
            </h3>
            <dl className="space-y-1">
              {g.rows.map(([keys, what]) => (
                <div key={keys} className="flex items-baseline gap-3">
                  <dt className="w-32 shrink-0">
                    <kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
                      {keys}
                    </kbd>
                  </dt>
                  <dd className="text-sm text-slate-600 dark:text-slate-300">{what}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
        <p className="text-xs text-slate-400 dark:text-slate-500">
          Tip: press <kbd className="font-mono">?</kbd> anywhere to open this
          again.
        </p>
      </div>
    </Modal>
  );
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el.isContentEditable
  );
}

const OPEN_EVENT = "johndo:show-shortcuts";

/** For buttons elsewhere in the UI (e.g. the tree header ⌨ button). */
export function openShortcutHelp(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

/** Mounts the global "?" listener and renders the modal when open. */
export function ShortcutHelp() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "?" && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);
  if (!open) return null;
  return <ShortcutHelpModal onClose={() => setOpen(false)} />;
}
